import { LitElement, html, css, nothing } from 'lit';
import { base, buttons, centerState } from '../styles.js';
import { LABELS } from '../states.js';

/**
 * The confirm dialog behind the footer's Close and Cancel (cert 1.13): scrim, centred card,
 * focus trap, Escape, and focus handed back to whatever opened it. It owns no decision -- it
 * dispatches `confirm` or `cancel` and the state machine does the rest.
 */
export class ConfirmDialog extends LitElement {
  static properties = {
    open: { type: Boolean, reflect: true },
    heading: { type: String },
    body: { type: String },
    confirmLabel: { type: String },
    cancelLabel: { type: String },
    danger: { type: Boolean },
    icon: { type: Object },
  };

  static styles = [base, buttons, centerState, css`
    .dialog-root {
      position: fixed; inset: 0; z-index: 300; display: flex; align-items: center; justify-content: center;
      padding: 20px;
    }
    .dialog-scrim {
      position: absolute; inset: 0; background: rgba(13, 23, 38, .5); backdrop-filter: blur(2px);
      animation: fadeIn .2s ease;
    }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .dialog {
      position: relative; width: 420px; max-width: 100%; background: var(--pbg-surface);
      border-radius: var(--pbg-radius-lg); box-shadow: var(--pbg-shadow-lg); padding: 22px;
      animation: dlgIn .26s cubic-bezier(.2, .7, .2, 1);
    }
    @keyframes dlgIn { from { opacity: 0; transform: scale(.96) translateY(8px); } to { opacity: 1; transform: none; } }
    @media (prefers-reduced-motion: reduce) { .dialog, .dialog-scrim { animation: none; } }
    .dialog .cs-ico { width: 42px; height: 42px; font-size: 20px; margin-bottom: 14px; }
    .dialog h3 { margin: 0 0 8px; font-family: var(--pbg-font-head); font-size: 18px; color: var(--pbg-navy); }
    .dialog p { margin: 0; font-size: 13.5px; color: var(--pbg-gray-700); line-height: 1.55; }
    .dlg-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 22px; flex-wrap: wrap; }
    .btn-danger { background: var(--pbg-danger); color: #fff; }
    .btn-danger:hover { background: #961f19; }
  `];

  #returnFocus = null;

  constructor() {
    super();
    this.open = false;
    this.heading = '';
    this.body = '';
    this.confirmLabel = 'Confirm';
    this.cancelLabel = LABELS.keepEditing;
    this.danger = false;
    this.icon = null;
  }

  updated(changed) {
    if (!changed.has('open')) return;
    if (this.open) {
      this.#returnFocus = deepActiveElement();
      // A beat after the open so the entrance animation has a node to land on (as the mock does).
      setTimeout(() => this.shadowRoot.querySelector('[data-dialog-action="confirm"]')?.focus(), 30);
    } else if (changed.get('open') === true) {
      this.#returnFocus?.focus?.({ preventScroll: true });
      this.#returnFocus = null;
    }
  }

  render() {
    if (!this.open) return nothing;
    return html`
      <div class="dialog-root" @keydown=${this.#onKeydown}>
        <div class="dialog-scrim" @click=${this.#cancel}></div>
        <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="dlg-title" aria-describedby="dlg-body">
          ${this.icon ? html`<div class="cs-ico ${this.icon.tone ?? 'blue'}" aria-hidden="true">${this.icon.glyph}</div>` : nothing}
          <h3 id="dlg-title">${this.heading}</h3>
          <p id="dlg-body">${this.body}</p>
          <div class="dlg-actions">
            <button class="btn btn-ghost" data-dialog-action="cancel" @click=${this.#cancel}>${this.cancelLabel}</button>
            <button class="btn ${this.danger ? 'btn-danger' : 'btn-primary'}" data-dialog-action="confirm"
                    @click=${this.#confirm}>${this.confirmLabel}</button>
          </div>
        </div>
      </div>`;
  }

  #confirm = () => {
    this.dispatchEvent(new CustomEvent('confirm', { bubbles: true, composed: true }));
  };

  #cancel = () => {
    this.open = false;
    this.dispatchEvent(new CustomEvent('cancel', { bubbles: true, composed: true }));
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') { event.preventDefault(); this.#cancel(); return; }
    if (event.key !== 'Tab') return;
    // Simple focus trap: the dialog has exactly its two buttons to cycle through.
    const focusable = [...this.shadowRoot.querySelectorAll('button')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = this.shadowRoot.activeElement;
    if (event.shiftKey && (active === first || !active)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (active === last || !active)) { event.preventDefault(); first.focus(); }
  };
}

/** The focused element even when it sits inside a shadow root (the footer buttons do). */
function deepActiveElement() {
  let el = document.activeElement;
  while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement;
  return el;
}

customElements.define('epc-confirm-dialog', ConfirmDialog);
